import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";

function readOnlineStatus() {
  if (typeof navigator?.onLine !== "boolean") {
    return true;
  }

  return navigator.onLine;
}

function describeError(error) {
  if (!error) return "";
  if (typeof error === "string") return error;

  return [error.name, error.message, error.code, error.type, error.request]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");
}

function isLikelyConnectionIssue(error, isOnline) {
  if (isOnline === false) {
    return true;
  }

  const normalized = describeError(error).toLowerCase();

  return [
    "err_internet_disconnected",
    "networkerror",
    "network request failed",
    "failed to fetch",
    "failed to load dynamically imported module",
    "importing a module script failed",
    "err_network_changed",
    "err_connection_reset",
    "offline",
  ].some((token) => normalized.includes(token));
}

class RemotePodLoadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return this.props.renderError(this.state.error);
    }

    return this.props.children;
  }
}

function RemotePodLoadingState({ isOnline }) {
  const { t } = useTranslation();

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-center"
      role="status"
      aria-live="polite"
    >
      <ClipLoader size={48} />
      {isOnline === false ? (
        <p className="max-w-md text-sm text-greyscale-2">
          {t("recordingRemoteLoad.loadingOffline", {
            defaultValue:
              "You're offline. Reconnect to continue loading the recording.",
          })}
        </p>
      ) : null}
    </div>
  );
}

function RemotePodLoadErrorState({ error, isOnline, onRetry }) {
  const { t } = useTranslation();
  const connectionIssue = isLikelyConnectionIssue(error, isOnline);

  return (
    <section
      aria-label="Recording load error"
      className="m-auto flex w-full max-w-lg flex-col items-start gap-3 p-4"
      data-testid="remote-pod-load-error"
    >
      <h1 className="title-text">
        {t("recordingRemoteLoad.title", {
          defaultValue: "Recording unavailable",
        })}
      </h1>
      <p>
        {connectionIssue
          ? t("recordingRemoteLoad.connectionBody", {
              defaultValue:
                "Your connection looks offline or unstable, so the recording could not open.",
            })
          : t("recordingRemoteLoad.body", {
              defaultValue:
                "We couldn't open the recording right now. Please try again.",
            })}
      </p>
      {connectionIssue ? (
        <p className="muted-text">
          {t("recordingRemoteLoad.connectionHint", {
            defaultValue: "Check your connection, then try again.",
          })}
        </p>
      ) : null}
      <button type="button" className="small-primary-button" onClick={onRetry}>
        {t("results.retry", { defaultValue: "Try again" })}
      </button>
    </section>
  );
}

export default function RemotePodLoader({
  load,
  componentProps = {},
  reloadPage = null,
}) {
  const [isOnline, setIsOnline] = useState(readOnlineStatus);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const LazyPod = useMemo(() => lazy(load), [load]);
  const handleRetry = useCallback(() => {
    if (typeof reloadPage === "function") {
      reloadPage();
      return;
    }

    if (
      typeof window !== "undefined" &&
      typeof window.location?.reload === "function"
    ) {
      window.location.reload();
    }
  }, [reloadPage]);

  return (
    <RemotePodLoadBoundary
      renderError={(error) => (
        <RemotePodLoadErrorState
          error={error}
          isOnline={isOnline}
          onRetry={handleRetry}
        />
      )}
    >
      <Suspense fallback={<RemotePodLoadingState isOnline={isOnline} />}>
        <LazyPod {...componentProps} />
      </Suspense>
    </RemotePodLoadBoundary>
  );
}
